import { useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Dialog, MenuItem, Select } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import type {
  CustomerPaymentMethodStatus,
  DashboardCustomerPaymentMethod,
} from '@models/customers';
import { CustomerPaymentMethodStatuses } from '@models/customers';
import { UserPermissions } from '@models/user';
import { useUpdateCustomerPaymentMethodMutation } from '@features/customers/mutations';
import { getPaymentMethodLabel } from '@features/customers/helpers';
import { formatPaymentDate } from '@features/payments/helpers';
import { useMeStore } from '@features/users/store';
import { userHasPermission } from '@utils/access';

import s from './EditPaymentMethodButton.module.css';

type EditPaymentMethodButtonProps = {
  method: DashboardCustomerPaymentMethod;
};

function formatBlockLabel(value: Date): string {
  const fd = formatPaymentDate(value);
  return `${fd.date} · ${fd.time}`;
}

function EditPaymentMethodButton({ method }: EditPaymentMethodButtonProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<CustomerPaymentMethodStatus>(
    CustomerPaymentMethodStatuses.ACTIVE,
  );
  const [preventTill, setPreventTill] = useState<Date | null>(null);
  const updateMethod = useUpdateCustomerPaymentMethodMutation();
  const me = useMeStore();

  // Editing a payment method is a write action
  if (!me || !userHasPermission(me, UserPermissions.EDIT_USERS)) return null;

  const originalTillMs = method.preventRecurringPaymentsTill
    ? new Date(method.preventRecurringPaymentsTill).getTime()
    : null;

  const openDialog = () => {
    setStatus(
      method.status === CustomerPaymentMethodStatuses.INACTIVE
        ? CustomerPaymentMethodStatuses.INACTIVE
        : CustomerPaymentMethodStatuses.ACTIVE,
    );
    setPreventTill(
      method.preventRecurringPaymentsTill ? new Date(method.preventRecurringPaymentsTill) : null,
    );
    setOpen(true);
  };

  const tillValid = preventTill == null || !Number.isNaN(preventTill.getTime());
  const statusChanged = status !== method.status;
  // The backend applies only provided fields (and can't clear the column), so an emptied picker
  // simply isn't sent — only a new, different timestamp counts as a change.
  const tillChanged = preventTill != null && tillValid && preventTill.getTime() !== originalTillMs;
  const hasChanges = statusChanged || tillChanged;

  const blockActive = originalTillMs != null && originalTillMs > Date.now();

  const handleSave = () => {
    updateMethod.mutate(
      {
        customerPaymentMethodId: method.id,
        ...(statusChanged ? { status } : {}),
        ...(tillChanged && preventTill
          ? { preventRecurringPaymentsTill: preventTill.toISOString() }
          : {}),
      },
      { onSuccess: () => setOpen(false) },
    );
  };

  return (
    <>
      <button
        type="button"
        className={s.editBtn}
        onClick={openDialog}
        title="Edit payment method"
        aria-label="Edit payment method">
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '14px',
              width: 440,
              maxWidth: '92vw',
              boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)',
            },
          },
        }}>
        <div className={s.body}>
          <h3 className={s.title}>Edit payment method</h3>
          <p className={s.subtitle}>
            {getPaymentMethodLabel(method)}
            {method.provider ? ` · ${method.provider}` : ''}
          </p>

          <div className={s.field}>
            <span className={s.fieldLabel}>Status</span>
            <Select
              value={status}
              size="small"
              fullWidth
              onChange={(event) => setStatus(event.target.value as CustomerPaymentMethodStatus)}>
              <MenuItem value={CustomerPaymentMethodStatuses.ACTIVE}>Active</MenuItem>
              <MenuItem value={CustomerPaymentMethodStatuses.INACTIVE}>Inactive</MenuItem>
            </Select>
          </div>

          <div className={s.field}>
            <div className={s.fieldLabelRow}>
              <span className={s.fieldLabel}>Prevent recurring payments till</span>
              <button type="button" className={s.nowBtn} onClick={() => setPreventTill(new Date())}>
                Set to now
              </button>
            </div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                value={preventTill}
                onChange={setPreventTill}
                ampm={false}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
            <span className={s.hint}>
              {blockActive
                ? `Recurring charges are blocked till ${formatBlockLabel(new Date(originalTillMs!))}.`
                : 'No active recurring-payment block.'}{' '}
              A past time (e.g. now) lifts the block.
            </span>
          </div>

          <div className={s.actions}>
            <button type="button" className={s.cancelBtn} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={s.saveBtn}
              onClick={handleSave}
              disabled={!hasChanges || !tillValid || updateMethod.isPending}>
              {updateMethod.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default EditPaymentMethodButton;
